import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { getImageUrl } from '../utils/imageUtils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent } from './ui/dialog';
import { Label } from './ui/label';
import { X, Minimize2, Maximize2, Send, Upload, UserX, Ban, Gamepad2, Smile, Image as ImageIcon, Phone, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import EmojiPicker from 'emoji-picker-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import ReactPlayer from 'react-player';
import Linkify from 'react-linkify';
import URLViewerModal from './URLViewerModal';
import useSendMessage from '../hooks/useSendMessage';
import useVoipMesh from '../hooks/useVoipMesh';

// Klipy API key for GIF search
const KLIPY_API_KEY = process.env.REACT_APP_KLIPY_API_KEY || '';

// Media Lightbox Component for viewing images/GIFs/videos in full size
const MediaLightbox = ({ media, onClose }) => {
  if (!media) return null;
  
  const isVideo = media.url?.match(/\.(mp4|webm|mov)$/i);
  const isGif = media.url?.match(/\.gif$/i);
  
  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 bg-black/50 rounded-full p-2"
      >
        <X className="w-8 h-8" />
      </button>
      <div 
        className="max-w-[90vw] max-h-[90vh] relative"
        onClick={(e) => e.stopPropagation()}
      >
        {isVideo ? (
          <video 
            src={getImageUrl(media.url)} 
            controls 
            autoPlay
            className="max-w-full max-h-[90vh] rounded-lg"
          />
        ) : (
          <img 
            src={getImageUrl(media.url)} 
            alt="Media full view" 
            className={`max-w-full max-h-[90vh] rounded-lg ${isGif ? '' : 'object-contain'}`}
          />
        )}
      </div>
    </div>
  );
};

const PMWindow = ({ friend, onClose, onMinimize, isMinimized, ws, user, isMaximized, onMaximize }) => {
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [pmSettings, setPmSettings] = useState(null);
  const [backgroundUrl, setBackgroundUrl] = useState('');
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [activeTab, setActiveTab] = useState('chat');
  const [videoAction, setVideoAction] = useState('');
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGiphyPicker, setShowGiphyPicker] = useState(false);
  const [giphySearch, setGiphySearch] = useState('');
  const [klipyResults, setKlipyResults] = useState([]);
  const [klipyLoading, setKlipyLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState(null); // For media lightbox
  const [friendIsTyping, setFriendIsTyping] = useState(false); // Typing indicator
  const [typingTimeout, setTypingTimeout] = useState(null); // For debouncing typing indicator
  const [isVideoPlaying, setIsVideoPlaying] = useState(false); // ReactPlayer playing state
  const [videoReady, setVideoReady] = useState(false); // Track if video is ready
  const [videoPlayerFailed, setVideoPlayerFailed] = useState(false); // Track if ReactPlayer failed
  const [urlModalOpen, setUrlModalOpen] = useState(false); // URL viewer modal
  const [viewUrl, setViewUrl] = useState(''); // URL to view in modal
  const [pmCallInfo, setPmCallInfo] = useState(null);
  const [pmInCall, setPmInCall] = useState(false);
  const [pmMuted, setPmMuted] = useState(false);
  const videoPlayerRef = useRef(null); // ReactPlayer ref
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const initRef = useRef(null);
  const sendMessage = useSendMessage(ws);

  useEffect(() => {
    if (!friend?.callId) return;
    setPmCallInfo((prev) => prev || {
      id: friend.callId,
      host_id: friend.id,
      participants: Array.isArray(friend.callParticipants) && friend.callParticipants.length > 0
        ? friend.callParticipants
        : [user?.id, friend.id].filter(Boolean),
      status: 'active'
    });
    setPmInCall(true);
  }, [friend?.callId, friend?.callParticipants, friend?.id, user?.id]);

  // Handle link clicks - open in URL viewer modal
  const handleLinkClick = useCallback((url) => {
    try {
      const urlObj = new URL(url);
      if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
        setViewUrl(url);
        setUrlModalOpen(true);
      } else {
        toast.error('This link type is not allowed for security reasons');
      }
    } catch {
      toast.error('Invalid URL');
    }
  }, []);

  // Link decorator for Linkify
  const linkDecorator = useCallback((decoratedHref, decoratedText, key) => (
    <a
      key={key}
      href={decoratedHref}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleLinkClick(decoratedHref);
      }}
      className="text-blue-400 hover:text-blue-300 underline break-all cursor-pointer"
      rel="noopener noreferrer"
      target="_blank"
      title={`Open: ${decoratedHref}`}
    >
      {decoratedText}
    </a>
  ), [handleLinkClick]);

  // Determine if background is dark to adjust text color
  const getContrastingTextColor = (hasBackground) => {
    if (!hasBackground) {
      return 'text-gray-800'; // Default light background
    }
    // If there's a background image, use white text for better visibility
    return 'text-white drop-shadow-lg';
  };

  const hasBackground = backgroundUrl || pmSettings?.background_url;

  const { remoteStreams, setMuted, closeAll } = useVoipMesh({
    ws,
    user,
    enabled: Boolean(pmInCall && pmCallInfo?.id),
    callId: pmCallInfo?.id,
    scope: 'pm',
    targetId: friend?.id,
    peerIds: pmCallInfo?.participants || []
  });

  useEffect(() => {
    setMuted(pmMuted);
  }, [pmMuted, setMuted]);

  // Send PM focus state when component mounts/unmounts
  useEffect(() => {
    if (friend && ws && ws.readyState === WebSocket.OPEN) {
      // Notify server that user is focused on this PM
      sendMessage({
        type: 'pm_focus',
        friend_id: friend.id,
        is_focused: true
      });
      
      // Mark all unread messages as read
      const unreadMsgIds = messages
        .filter(m => m.sender_id === friend.id && !m.read && m.status !== 'read')
        .map(m => m.id);
      
      if (unreadMsgIds.length > 0) {
        sendMessage({
          type: 'pm_read',
          friend_id: friend.id,
          message_ids: unreadMsgIds
        });
      }
    }
    
    return () => {
      // Notify server that user left this PM
      if (ws && ws.readyState === WebSocket.OPEN && friend) {
        sendMessage({
          type: 'pm_focus',
          friend_id: friend.id,
          is_focused: false
        });
      }
    };
  }, [friend?.id, ws, sendMessage, messages]);

useEffect(() => {
    // 1. Safety Checks
    if (!friend?.id) return;

    // 2. THE LOCK: If we already initialized this user, STOP.
    if (initRef.current === friend.id) {
        return; 
    }

    // 3. Set the lock immediately
    initRef.current = friend.id;

    // 4. Run Logic (Fetch messages, Join Socket, etc.)
    console.log("Initializing chat with:", friend.username); // Debug log
    fetchMessages();
    sendMessage({ type: 'join_pm', friendId: friend.id });
    fetchPMSettings();
    fetchGames();

    // Ensure no state setting inside here triggers this effect again!
}, [friend?.id, ws, sendMessage]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadActivePmCall = useCallback(async () => {
    if (!friend?.id || user?.role === 'guest') return;
    try {
      const response = await axios.get(`${API}/voip/active`, {
        params: { scope: 'pm', target_id: friend.id }
      });
      if (response.data?.active && response.data?.call) {
        setPmCallInfo(response.data.call);
        setPmInCall(Boolean(response.data.is_participant));
      } else {
        setPmCallInfo(null);
        setPmInCall(false);
      }
    } catch {
      // Keep current in-call state on transient polling errors.
    }
  }, [friend?.id, user?.role]);

  useEffect(() => {
    loadActivePmCall();
    const id = setInterval(loadActivePmCall, 8000);
    return () => clearInterval(id);
  }, [loadActivePmCall]);

  useEffect(() => {
    return () => {
      if (pmInCall && pmCallInfo?.id) {
        axios.post(`${API}/voip/${pmCallInfo.id}/leave`).catch(() => {});
      }
      closeAll();
    };
  }, [pmInCall, pmCallInfo?.id, closeAll]);

  // Listen for video sync and game sync events via WebSocket
  useEffect(() => {
    if (!ws) return;

    const handleMessage = (event) => {
      const data = JSON.parse(event.data);
      
      console.log('[PMWindow] Received WS message:', data.type, 'from:', data.sender_id, 'friend:', friend.id);
      
      // Handle video sync
      if (data.type === 'video_sync' && data.sender_id === friend.id) {
        console.log('[PMWindow] Handling video sync:', data);
        handleVideoSync(data);
      }
      
      // Handle game sync
      if (data.type === 'game_sync' && data.sender_id === friend.id) {
        console.log('[PMWindow] Handling game sync:', data);
        handleGameSync(data);
      }

      if (data.type === 'voip_call_end' && data.call_id && pmCallInfo?.id === data.call_id) {
        setPmInCall(false);
        setPmMuted(false);
        setPmCallInfo(null);
        closeAll();
        toast.info('PM call ended');
      }

      if (data.type === 'voip_call_reject' && data.call_id && pmCallInfo?.id === data.call_id) {
        setPmInCall(false);
        setPmMuted(false);
        closeAll();
        toast.info(`${friend.username} rejected the call`);
      }
      
      // Handle video launch notification
      if (data.type === 'video_launch' && (data.sender_id === friend.id || data.sender_id === user.id)) {
        console.log('[PMWindow] Received video launch:', data);
        setIsExtracting(false); // Stop loading indicator
        
        toast.info(`Video shared by ${data.sender_id === user.id ? 'you' : friend.username} is loading...`, {
          duration: 3000,
          icon: '🎬'
        });
        
        // Switch to video tab and set URL
        setActiveTab('video');
        setVideoUrl(data.video_url); // Use the extracted URL for the player
        setVideoReady(false);
        
        // Start playing with ReactPlayer
        setIsVideoPlaying(true);
        
        // Seek to timestamp after a short delay to let player initialize
        if (data.timestamp && data.timestamp > 0) {
          setTimeout(() => {
            if (videoPlayerRef.current) {
              videoPlayerRef.current.seekTo(data.timestamp, 'seconds');
            }
          }, 1000);
        }
      }
      
      // Handle game launch notification
      if (data.type === 'game_launch' && data.sender_id === friend.id) {
        console.log('[PMWindow] Received game launch from friend:', data);
        const game = games.find(g => g.id === data.game_id);
        if (game) {
          toast.info(`${friend.username} launched ${game.name}. Joining...`, {
            duration: 3000
          });
          setActiveTab('games');
          setSelectedGame(game);
        } else {
          console.warn('[PMWindow] Game not found:', data.game_id);
        }
      }
      
      if (data.type === 'pm' && data.message.sender_id === friend.id) {
        setMessages(prev => {
          // Check if message already exists to avoid duplicates
          const exists = prev.some(m => m.id === data.message.id);
          if (exists) return prev;
          return [...prev, data.message];
        });
        
        // Mark message as read since we're viewing this PM
        sendMessage({
          type: 'pm_read',
          friend_id: friend.id,
          message_ids: [data.message.id]
        });
      }
      
      // Handle PM typing indicator
      if (data.type === 'pm_typing' && data.sender_id === friend.id) {
        setFriendIsTyping(data.is_typing);
        // Auto-hide typing indicator after 3 seconds
        if (data.is_typing) {
          setTimeout(() => setFriendIsTyping(false), 3000);
        }
      }
      
      // Handle PM status updates (delivered/read)
      if (data.type === 'pm_status_update' && data.sender_id === friend.id) {
        setMessages(prev => prev.map(msg => {
          if (msg.sender_id === user.id && msg.recipient_id === friend.id) {
            if (data.status === 'delivered' && msg.status === 'sent') {
              return { ...msg, status: 'delivered' };
            }
            if (data.status === 'read') {
              if (data.message_ids && data.message_ids.includes(msg.id)) {
                return { ...msg, status: 'read', read: true };
              }
              // If no specific message_ids, mark all as read
              if (!data.message_ids) {
                return { ...msg, status: 'read', read: true };
              }
            }
          }
          return msg;
        }));
      }
      
      // Also handle pm_sent for messages we sent
      if (data.type === 'pm_sent' && data.message.recipient_id === friend.id) {
        setMessages(prev => {
          // Check if message already exists by ID
          const existsById = prev.some(m => m.id === data.message.id);
          if (existsById) return prev;
          
          // Check if this is a confirmation of an optimistic message (same content, same sender)
          const optimisticIndex = prev.findIndex(m => 
            m._optimistic && 
            m.sender_id === data.message.sender_id &&
            m.content === data.message.content
          );
          
          if (optimisticIndex !== -1) {
            // Replace optimistic message with real one from server
            const newMessages = [...prev];
            // Set status based on whether it was delivered
            newMessages[optimisticIndex] = {
              ...data.message,
              status: data.delivered ? 'delivered' : 'sent'
            };
            return newMessages;
          }
          
          return [...prev, { ...data.message, status: data.delivered ? 'delivered' : 'sent' }];
        });
      }
    };

    ws.addEventListener('message', handleMessage);
    return () => ws.removeEventListener('message', handleMessage);
  }, [ws, friend.id, friend.username, games, user.id, sendMessage, pmCallInfo?.id, closeAll]);

  useEffect(() => {
    const onLivePm = (event) => {
      const data = event?.detail;
      if (!data || !data.message) return;

      const message = data.message;
      const senderId = message.sender_id;
      const recipientId = message.recipient_id;
      const belongsToThisWindow =
        (senderId === friend.id && recipientId === user.id) ||
        (senderId === user.id && recipientId === friend.id);

      if (!belongsToThisWindow) return;

      if (data.type === 'pm') {
        setMessages((prev) => {
          if (prev.some((m) => m.id === message.id)) return prev;
          return [...prev, message];
        });

        sendMessage({
          type: 'pm_read',
          friend_id: friend.id,
          message_ids: [message.id]
        });
      }

      if (data.type === 'pm_sent') {
        setMessages((prev) => {
          const existsById = prev.some((m) => m.id === message.id);
          if (existsById) return prev;

          const optimisticIndex = prev.findIndex((m) =>
            m._optimistic &&
            m.sender_id === message.sender_id &&
            m.content === message.content
          );

          if (optimisticIndex !== -1) {
            const next = [...prev];
            next[optimisticIndex] = {
              ...message,
              status: data.delivered ? 'delivered' : 'sent'
            };
            return next;
          }

          return [...prev, { ...message, status: data.delivered ? 'delivered' : 'sent' }];
        });
      }
    };

    window.addEventListener('pm-live-event', onLivePm);
    return () => window.removeEventListener('pm-live-event', onLivePm);
  }, [friend.id, user.id, sendMessage]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/pm/${friend.id}/messages?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchPMSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/pm/settings/${friend.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPmSettings(res.data);
      if (res.data.background_url) {
        setBackgroundUrl(res.data.background_url);
      }
    } catch (error) {
      console.error('Error fetching PM settings:', error);
    }
  };

  const fetchGames = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/games`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGames(res.data);
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  // Send typing indicator
  const sendTypingIndicator = (isTyping) => {
    sendMessage({
      type: 'pm_typing',
      recipient_id: friend.id,
      is_typing: isTyping
    });
  };

  // Handle input change with typing indicator
  const handleInputChange = (e) => {
    setMessageInput(e.target.value);
    
    // Send typing indicator
    sendTypingIndicator(true);
    
    // Clear existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    // Set new timeout to stop typing indicator after 2 seconds of no input
    const timeout = setTimeout(() => {
      sendTypingIndicator(false);
    }, 2000);
    setTypingTimeout(timeout);
  };

  const handleSendMessage = () => {
    if (!messageInput.trim()) return;

    // Stop typing indicator
    sendTypingIndicator(false);
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    sendMessage({
      type: 'pm',
      recipient_id: friend.id,
      content: messageInput
    });

    const newMsg = {
      id: `temp-${Date.now()}`,  // Temporary ID for optimistic message
      sender_id: user.id,
      recipient_id: friend.id,
      content: messageInput,
      created_at: new Date().toISOString(),
      sender: user,
      status: 'sent',  // Initial status
      _optimistic: true  // Mark as optimistic for deduplication
    };

    setMessages(prev => [...prev, newMsg]);
    setMessageInput('');
  };

  const startPmCall = async () => {
    try {
      const res = await axios.post(`${API}/voip/start`, {
        scope: 'pm',
        target_id: friend.id
      });
      const joinLink = res?.data?.join_link || '';
      setPmCallInfo({
        id: res?.data?.call_id,
        host_id: user.id,
        participants: [user.id],
        status: 'active'
      });
      setPmInCall(true);
      sendMessage({
        type: 'voip_call_invite',
        call_id: res?.data?.call_id,
        scope: 'pm',
        target_id: friend.id,
        recipient_id: friend.id
      });
      toast.success(joinLink ? `PM call started (${joinLink})` : 'PM call started');
      loadActivePmCall();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to start PM call');
    }
  };

  const joinPmCall = async () => {
    if (!pmCallInfo?.id) return;
    try {
      await axios.post(`${API}/voip/${pmCallInfo.id}/join`);
      setPmInCall(true);
      toast.success('Joined PM call');
      loadActivePmCall();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to join PM call');
    }
  };

  const leavePmCall = async () => {
    if (!pmCallInfo?.id) return;
    try {
      await axios.post(`${API}/voip/${pmCallInfo.id}/leave`);
      setPmInCall(false);
      setPmMuted(false);
      closeAll();
      toast.success('Left PM call');
      loadActivePmCall();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to leave PM call');
    }
  };

  const endPmCall = async () => {
    if (!pmCallInfo?.id) return;
    try {
      await axios.post(`${API}/voip/${pmCallInfo.id}/end`);
      sendMessage({
        type: 'voip_call_end',
        call_id: pmCallInfo.id,
        scope: 'pm',
        target_id: friend.id,
        recipient_id: friend.id
      });
      setPmInCall(false);
      setPmMuted(false);
      setPmCallInfo(null);
      closeAll();
      toast.success('PM call ended');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to end PM call');
    }
  };

  const handleVideoSync = (data) => {
    console.log('[PMWindow] handleVideoSync called:', data);
    
    // ReactPlayer handles all video types including YouTube
    // So we can sync across all supported platforms
    
    switch (data.action) {
      case 'play':
        if (data.video_url && data.video_url !== videoUrl) {
          console.log('[PMWindow] Setting video URL:', data.video_url);
          setVideoUrl(data.video_url);
        }
        console.log('[PMWindow] Playing video');
        setIsVideoPlaying(true);
        // Seek to timestamp if provided
        if (data.timestamp && videoPlayerRef.current && typeof videoPlayerRef.current.seekTo === 'function') {
          setTimeout(() => {
            try {
              videoPlayerRef.current?.seekTo(data.timestamp, 'seconds');
            } catch (e) {
              console.warn('[PMWindow] Seek failed:', e);
            }
          }, 500);
        }
        break;
      case 'pause':
        console.log('[PMWindow] Pausing video');
        setIsVideoPlaying(false);
        break;
      case 'seek':
        console.log('[PMWindow] Seeking to:', data.timestamp);
        if (videoPlayerRef.current && typeof videoPlayerRef.current.seekTo === 'function') {
          try {
            videoPlayerRef.current.seekTo(data.timestamp, 'seconds');
          } catch (e) {
            console.warn('[PMWindow] Seek failed:', e);
          }
        }
        break;
      case 'stop':
        console.log('[PMWindow] Stopping video');
        setIsVideoPlaying(false);
        if (videoPlayerRef.current && typeof videoPlayerRef.current.seekTo === 'function') {
          try {
            videoPlayerRef.current.seekTo(0, 'seconds');
          } catch (e) {
            console.warn('[PMWindow] Seek failed:', e);
          }
        }
        break;
      default:
        break;
    }
  };

  const handleGameSync = (data) => {
    if (data.game_id) {
      const game = games.find(g => g.id === data.game_id);
      if (game) {
        setSelectedGame(game);
        setActiveTab('games');
      }
    }
  };

  const sendVideoSync = (action, timestamp = 0, url = null) => {
    // Get current time from ReactPlayer if available
    // ReactPlayer's ref exposes getCurrentTime() method, but player must be ready
    let currentTime = timestamp;
    if (videoPlayerRef.current && typeof videoPlayerRef.current.getCurrentTime === 'function' && action !== 'stop') {
      try {
        currentTime = videoPlayerRef.current.getCurrentTime() || timestamp;
      } catch (e) {
        console.warn('[PMWindow] Could not get current time from player:', e);
        currentTime = timestamp;
      }
    }

    const syncData = {
      type: 'video_sync',
      recipient_id: friend.id,
      action,
      timestamp: currentTime,
      video_url: url || videoUrl  // Use current videoUrl if not provided
    };
    
    console.log('[PMWindow] Sending video sync:', syncData);
    sendMessage(syncData);
  };

  const sendVideoLaunchNotification = (videoUrl, timestamp = 0) => {
    console.log('[PMWindow] Sending video launch to:', friend.id, 'URL:', videoUrl);
    sendMessage({
      type: 'video_launch',
      recipient_id: friend.id,
      video_url: videoUrl,
      timestamp
    });
  };

  const sendGameLaunchNotification = (gameId) => {
    console.log('[PMWindow] Sending game launch to:', friend.id, 'Game ID:', gameId);
    sendMessage({
      type: 'game_launch',
      recipient_id: friend.id,
      game_id: gameId
    });
  };

  const handleVideoAction = (action) => {
    // Safely get current time - check if method exists
    let currentTime = 0;
    if (videoPlayerRef.current && typeof videoPlayerRef.current.getCurrentTime === 'function') {
      try {
        currentTime = videoPlayerRef.current.getCurrentTime() || 0;
      } catch (e) {
        console.warn('[PMWindow] Could not get current time:', e);
      }
    }
    
    switch (action) {
      case 'play':
        setIsVideoPlaying(true);
        sendVideoSync('play', currentTime, videoUrl);
        break;
      case 'pause':
        setIsVideoPlaying(false);
        sendVideoSync('pause', currentTime);
        break;
      case 'stop':
        setIsVideoPlaying(false);
        if (videoPlayerRef.current && typeof videoPlayerRef.current.seekTo === 'function') {
          try {
            videoPlayerRef.current.seekTo(0, 'seconds');
          } catch (e) {
            console.warn('[PMWindow] Could not seek:', e);
          }
        }
        sendVideoSync('stop');
        break;
      default:
        break;
    }
  };

  // Check if URL is supported by ReactPlayer
  // Check if URL is a direct video file or stream
  const isDirectVideoUrl = (url) => {
    if (!url) return false;
    const videoExtensions = /\.(mp4|webm|ogg|ogv|m4v|mov|avi|mkv|m3u8|mpd|ts|flv)(\?.*)?$/i;
    const streamPatterns = [
      /\.m3u8/i,  // HLS streams
      /\.mpd/i,   // DASH streams
      /blob:/i,   // Blob URLs
      /video/i,   // URLs containing 'video'
    ];
    
    // Check file extension
    if (videoExtensions.test(url)) return true;
    
    // Check stream patterns
    for (const pattern of streamPatterns) {
      if (pattern.test(url)) return true;
    }
    
    return false;
  };

  const isUrlSupported = (url) => {
    // First check if ReactPlayer natively supports it
    if (ReactPlayer.canPlay(url)) return true;
    
    // Then check if it's a direct video URL
    if (isDirectVideoUrl(url)) return true;
    
    // For any other URL, we'll try to play it anyway
    // Many video URLs don't have obvious extensions but still work
    return true; // Be permissive - let the player try to handle it
  };

  // Check if URL is Dailymotion
  const isDailymotionUrl = (url) => {
    return url && (url.includes('dailymotion.com') || url.includes('dai.ly'));
  };

  // Extract Dailymotion video ID from URL
  const getDailymotionVideoId = (url) => {
    // Handle: https://www.dailymotion.com/video/x9qq0m2 or https://dai.ly/x9qq0m2
    const patterns = [
      /dailymotion\.com\/video\/([a-zA-Z0-9]+)/,
      /dai\.ly\/([a-zA-Z0-9]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Clean and normalize video URL
  const normalizeVideoUrl = (url) => {
    if (!url) return '';
    let cleanUrl = url.trim();
    
    // Remove any extra whitespace or line breaks
    cleanUrl = cleanUrl.replace(/\s+/g, '');
    
    // Handle common URL formats
    // YouTube: https://www.youtube.com/watch?v=VIDEO_ID or https://youtu.be/VIDEO_ID
    // Vimeo: https://vimeo.com/VIDEO_ID
    // Dailymotion: https://www.dailymotion.com/video/VIDEO_ID
    
    return cleanUrl;
  };

  const loadVideo = () => {
    if (!videoUrl.trim()) {
      toast.error('Please enter a video URL');
      return;
    }
    
    // Normalize the URL
    const cleanUrl = normalizeVideoUrl(videoUrl);
    console.log('[PMWindow] Loading video:', cleanUrl);
    
    // Set loading state and send original URL to backend for extraction
    setIsExtracting(true);
    setVideoPlayerFailed(false);
    
    // Send the original URL to the backend
    sendVideoLaunchNotification(cleanUrl, 0);
    toast.info(`Extracting video from URL...`);
  };

  const updateBackground = async (isUrl = true) => {
    try {
      const token = localStorage.getItem('token');
      let url = backgroundUrl;

      if (!isUrl && backgroundFile) {
        const formData = new FormData();
        formData.append('file', backgroundFile);
        const uploadRes = await axios.post(`${API}/media/upload-background`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        url = uploadRes.data.url;
      }

      await axios.put(`${API}/pm/settings/${friend.id}/background`, {
        background_url: url
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Background updated!');
      fetchPMSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update background');
    }
  };

  const clearChat = async () => {
    if (!window.confirm('Are you sure you want to clear all messages?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/pm/${friend.id}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages([]);
      toast.success('Chat cleared');
    } catch (error) {
      toast.error('Failed to clear chat');
    }
  };

  const clearBackground = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/pm/settings/${friend.id}/background`, {
        background_url: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackgroundUrl('');
      toast.success('Background cleared');
      fetchPMSettings();
    } catch (error) {
      toast.error('Failed to clear background');
    }
  };

  const blockUser = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/pm/settings/${friend.id}/block`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User blocked');
      onClose();
    } catch (error) {
      toast.error('Failed to block user');
    }
  };

  const unfriend = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/friends/${friend.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Friend removed');
      onClose();
    } catch (error) {
      toast.error('Failed to remove friend');
    }
  };

  // Search GIFs using Klipy API
  const searchKlipyGifs = async (query) => {
    if (!query?.trim()) {
      // Load trending if no query
      try {
        setKlipyLoading(true);
        const response = await axios.get(`https://api.klipy.com/api/v1/${KLIPY_API_KEY}/gifs/trending`, {
          params: { limit: 16 }
        });
        // Klipy returns data.data array with file.md.gif.url structure
        const gifs = response.data.data?.data?.map(gif => ({
          id: gif.id || gif.slug,
          url: gif.file?.md?.gif?.url || gif.file?.sm?.gif?.url,
          title: gif.title || ''
        })) || [];
        setKlipyResults(gifs);
      } catch (error) {
        console.error('Klipy trending failed:', error);
      } finally {
        setKlipyLoading(false);
      }
      return;
    }
    
    try {
      setKlipyLoading(true);
      const response = await axios.get(`https://api.klipy.com/api/v1/${KLIPY_API_KEY}/gifs/search`, {
        params: { q: query, limit: 16 }
      });
      // Klipy returns data.data array with file.md.gif.url structure
      const gifs = response.data.data?.data?.map(gif => ({
        id: gif.id || gif.slug,
        url: gif.file?.md?.gif?.url || gif.file?.sm?.gif?.url,
        title: gif.title || ''
      })) || [];
      setKlipyResults(gifs);
    } catch (error) {
      console.error('Klipy search failed:', error);
      toast.error('Failed to search GIFs');
    } finally {
      setKlipyLoading(false);
    }
  };

  // Load trending GIFs when picker opens
  useEffect(() => {
    if (showGiphyPicker && klipyResults.length === 0) {
      searchKlipyGifs('');
    }
  }, [showGiphyPicker]);

  const sendGif = (gifUrl, e) => {
    if (e) e.preventDefault();
    
    // Add optimistic GIF message for instant display
    const newMsg = {
      id: `temp-gif-${Date.now()}`,
      sender_id: user.id,
      recipient_id: friend.id,
      content: gifUrl,
      created_at: new Date().toISOString(),
      sender: user,
      _optimistic: true
    };
    setMessages(prev => [...prev, newMsg]);
    
    sendMessage({
      type: 'pm',
      recipient_id: friend.id,
      content: gifUrl
    });
    setShowGiphyPicker(false);
    setKlipyResults([]);
    setGiphySearch('');
    toast.success('GIF sent!');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/media/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      const fileUrl = getImageUrl(response.data.url);
      
      // Send file URL via WebSocket
      sendMessage({
        type: 'pm',
        recipient_id: friend.id,
        content: fileUrl
      });
      
      toast.success('File uploaded!');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 bg-white shadow-lg rounded-t-lg border border-gray-300 w-64 cursor-pointer z-40"
        onClick={onMinimize}
      >
        <div className="p-3 bg-blue-600 text-white flex items-center justify-between rounded-t-lg">
          <span className="font-semibold truncate">{friend.username}</span>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onClose(); }} className="text-white hover:bg-blue-700">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Mobile detection
  const isMobile = window.innerWidth < 768;

  return (
    <div 
      className={`fixed bg-white dark:bg-gray-800 shadow-2xl border border-gray-300 dark:border-gray-700 z-50 flex flex-col ${
        isMobile
          ? 'inset-0 w-full h-screen rounded-none'
          : isMaximized 
            ? 'inset-4 rounded-lg' 
            : 'bottom-4 right-4 w-[500px] h-[600px] rounded-lg'
      }`}
      style={pmSettings?.background_url ? {
        backgroundImage: `url(${getImageUrl(pmSettings.background_url)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      } : {}}
    >
      {/* Header */}
      <div className="p-3 bg-blue-600 dark:bg-blue-700 text-white flex items-center justify-between rounded-t-lg">
        <span className="font-semibold">{friend.username}</span>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => (pmCallInfo ? (pmInCall ? leavePmCall() : joinPmCall()) : startPmCall())}
            className="text-white hover:bg-blue-700 dark:hover:bg-blue-800"
            title={pmCallInfo ? (pmInCall ? 'Leave PM call' : 'Join PM call') : 'Start PM call'}
          >
            <Phone className="w-4 h-4" />
          </Button>
          {!isMobile && (
            <>
              <Button variant="ghost" size="icon" onClick={onMinimize} className="text-white hover:bg-blue-700 dark:hover:bg-blue-800">
                <Minimize2 className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={onMaximize} className="text-white hover:bg-blue-700 dark:hover:bg-blue-800">
                <Maximize2 className="w-4 h-4" />
              </Button>
            </>
          )}
          <Button variant="ghost" size="icon" onClick={onClose} className={`text-white hover:bg-blue-700 dark:hover:bg-blue-800 ${isMobile ? 'scale-125' : ''}`}>
            <X className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
          </Button>
        </div>
      </div>

      {pmCallInfo && (
        <div className="px-3 py-2 border-b bg-blue-50 dark:bg-gray-700 dark:border-gray-600 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <span className={`font-semibold ${pmInCall ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-300'}`}>
              {pmInCall ? 'In PM call' : 'PM call active'}
            </span>
            <span className="text-gray-600 dark:text-gray-300">
              {(pmCallInfo.participants || []).length} participant{(pmCallInfo.participants || []).length === 1 ? '' : 's'}
            </span>
            <span className="text-gray-600 dark:text-gray-300">
              Audio peers: {Object.keys(remoteStreams || {}).length}
            </span>
            <span className={`font-medium ${pmMuted ? 'text-orange-600 dark:text-orange-400' : 'text-gray-600 dark:text-gray-300'}`}>
              {pmMuted ? 'Muted' : 'Unmuted'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {pmInCall && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setPmMuted((prev) => !prev)}
                title={pmMuted ? 'Unmute' : 'Mute'}
              >
                {pmMuted ? <VolumeX className="w-3 h-3 mr-1" /> : <Volume2 className="w-3 h-3 mr-1" />}
                {pmMuted ? 'Unmute' : 'Mute'}
              </Button>
            )}
            {pmInCall && pmCallInfo?.host_id === user.id && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs text-red-600"
                onClick={endPmCall}
              >
                End
              </Button>
            )}
            <Button
              variant={pmInCall ? 'outline' : 'default'}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={pmInCall ? leavePmCall : joinPmCall}
            >
              {pmInCall ? 'Leave' : 'Join'}
            </Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
        {Object.entries(remoteStreams).map(([peerId, stream]) => (
          <audio
            key={peerId}
            autoPlay
            playsInline
            ref={(el) => {
              if (!el) return;
              if (el.srcObject !== stream) {
                el.srcObject = stream;
              }
            }}
            style={{ display: 'none' }}
          />
        ))}
        <TabsList className="grid w-full grid-cols-4 dark:bg-gray-700 flex-shrink-0">
          <TabsTrigger value="chat" className="dark:text-gray-200 dark:data-[state=active]:bg-gray-600">Chat</TabsTrigger>
          <TabsTrigger value="games" className="dark:text-gray-200 dark:data-[state=active]:bg-gray-600">Games</TabsTrigger>
          <TabsTrigger value="video" className="dark:text-gray-200 dark:data-[state=active]:bg-gray-600">Video</TabsTrigger>
          <TabsTrigger value="options" className="dark:text-gray-200 dark:data-[state=active]:bg-gray-600">Options</TabsTrigger>
        </TabsList>

        {/* Chat Tab */}
        <TabsContent value="chat" className={`mt-0 data-[state=active]:flex data-[state=inactive]:hidden ${isMobile ? 'absolute inset-x-0 flex flex-col' : 'flex-1 flex flex-col'}`} style={isMobile ? { top: '108px', bottom: '68px' } : {}}>
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-3 pb-4">
              {messages.map((msg, idx) => (
                <div key={msg.id || idx} className={`flex gap-2 ${msg.sender_id === user.id ? 'flex-row-reverse' : ''}`}>
                  <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarImage src={getImageUrl(msg.sender_id === user.id ? user.avatar_url : friend.avatar_url)} />
                    <AvatarFallback className="bg-blue-600 text-white text-xs">
                      {msg.sender_id === user.id 
                        ? user.username?.substring(0, 2).toUpperCase() 
                        : friend.username?.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col max-w-[70%]">
                    <div className={`flex items-center gap-2 mb-1 ${msg.sender_id === user.id ? 'flex-row-reverse' : ''}`}>
                      <span className={`text-xs px-1 ${getContrastingTextColor(hasBackground)} dark:text-gray-300`}>
                        {msg.sender_id === user.id ? 'You' : friend.username}
                      </span>
                      <span className={`text-[10px] ${hasBackground ? 'text-white/70' : 'text-gray-400'} dark:text-gray-500`}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className={`px-4 py-2 rounded-2xl ${
                      msg.sender_id === user.id
                        ? 'bg-blue-600 dark:bg-blue-700 text-white'
                        : hasBackground 
                          ? 'bg-white/90 dark:bg-gray-700/90 backdrop-blur-sm border dark:border-gray-600' 
                          : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border dark:border-gray-600'
                    } ${hasBackground && msg.sender_id !== user.id ? getContrastingTextColor(false) : ''}`}>
                      {/* Image/GIF content with lightbox */}
                      {msg.content.startsWith('http') && (msg.content.includes('/media/') || msg.content.match(/\.(jpg|jpeg|png|gif)$/i) || msg.content.includes('klipy.com') || msg.content.includes('giphy.com')) ? (
                        <img 
                          src={getImageUrl(msg.content)} 
                          alt="media" 
                          className="rounded max-w-[150px] md:max-w-xs cursor-pointer hover:opacity-90 transition-opacity"
                          style={{ width: window.innerWidth < 768 ? '50%' : 'auto', maxWidth: window.innerWidth < 768 ? '150px' : '384px' }}
                          onClick={() => setLightboxMedia({ url: getImageUrl(msg.content), type: 'image' })}
                        />
                      ) : msg.content.startsWith('http') && msg.content.match(/\.(mp4|webm|mov)$/i) ? (
                        /* Video content with lightbox */
                        <video 
                          src={getImageUrl(msg.content)}
                          className="rounded max-w-[150px] md:max-w-xs cursor-pointer hover:opacity-90 transition-opacity"
                          style={{ width: window.innerWidth < 768 ? '50%' : 'auto', maxWidth: window.innerWidth < 768 ? '150px' : '384px' }}
                          onClick={() => setLightboxMedia({ url: getImageUrl(msg.content), type: 'video' })}
                          muted
                        />
                      ) : (
                        /* Text content with clickable links */
                        <Linkify componentDecorator={linkDecorator}>
                          {msg.content}
                        </Linkify>
                      )}
                      {/* Message status indicators (WhatsApp-style tick marks) - only for sent messages */}
                      {msg.sender_id === user.id && (
                        <div className="flex justify-end mt-1">
                          <span className="text-[10px] opacity-70 flex items-center gap-0.5">
                            {msg._optimistic ? (
                              // Sending... (clock icon)
                              <span title="Sending">⏱</span>
                            ) : msg.status === 'read' || msg.read ? (
                              // Read - double blue tick
                              <span className="text-blue-300" title="Read">✓✓</span>
                            ) : msg.status === 'delivered' ? (
                              // Delivered - double grey tick
                              <span title="Delivered">✓✓</span>
                            ) : (
                              // Sent - single grey tick
                              <span title="Sent">✓</span>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {/* Typing indicator */}
              {friendIsTyping && (
                <div className="flex gap-2 items-center">
                  <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarImage src={getImageUrl(friend.avatar_url)} />
                    <AvatarFallback className="bg-blue-600 text-white text-xs">
                      {friend.username?.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="px-4 py-2 rounded-2xl bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    <span className="typing-indicator flex gap-1">
                      <span className="animate-bounce" style={{ animationDelay: '0ms' }}>•</span>
                      <span className="animate-bounce" style={{ animationDelay: '150ms' }}>•</span>
                      <span className="animate-bounce" style={{ animationDelay: '300ms' }}>•</span>
                    </span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
          
          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div className={`absolute ${isMobile ? 'bottom-16 left-2 right-2' : 'bottom-16 left-4'} z-10`} style={isMobile ? { bottom: '72px' } : {}}>
              <EmojiPicker onEmojiClick={(emoji) => {
                // Send emoji immediately
                const newMsg = {
                  id: `temp-emoji-${Date.now()}`,
                  sender_id: user.id,
                  recipient_id: friend.id,
                  content: emoji.emoji,
                  created_at: new Date().toISOString(),
                  sender: user,
                  _optimistic: true
                };
                setMessages(prev => [...prev, newMsg]);
                sendMessage({
                  type: 'pm',
                  recipient_id: friend.id,
                  content: emoji.emoji
                });
                setShowEmojiPicker(false);
              }} />
            </div>
          )}
          
          {/* GIF Picker (Klipy) */}
          {showGiphyPicker && (
            <div className={`absolute ${isMobile ? 'bottom-16 left-2 right-2' : 'bottom-16 left-4 right-4'} bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg p-4 z-10 max-h-96 overflow-auto`} style={isMobile ? { bottom: '72px' } : {}}>
              {/* Klipy Attribution - Required for production API */}
              <div className="flex justify-between items-center mb-2">
                <img 
                  src="/klipy-powered-light.png" 
                  alt="Powered by KLIPY" 
                  className="h-5 dark:hidden"
                />
                <img 
                  src="/klipy-powered-dark.png" 
                  alt="Powered by KLIPY" 
                  className="h-5 hidden dark:block"
                />
                <Button variant="ghost" size="icon" onClick={() => { setShowGiphyPicker(false); setKlipyResults([]); setGiphySearch(''); }} className="dark:hover:bg-gray-700">
                  <X className="w-4 h-4 dark:text-gray-200" />
                </Button>
              </div>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Search KLIPY"
                  value={giphySearch}
                  onChange={(e) => setGiphySearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchKlipyGifs(giphySearch)}
                  className="flex-1 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:placeholder-gray-400"
                />
                <Button variant="outline" size="sm" onClick={() => searchKlipyGifs(giphySearch)}>
                  Search
                </Button>
              </div>
              {klipyLoading ? (
                <div className="text-center py-8 text-gray-500">Loading GIFs...</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {klipyResults.map((gif) => (
                    <img
                      key={gif.id}
                      src={getImageUrl(gif.url)}
                      alt={gif.title}
                      className="cursor-pointer rounded hover:opacity-75 hover:scale-105 transition-all w-full h-24 object-cover"
                      onClick={(e) => sendGif(gif.url, e)}
                    />
                  ))}
                </div>
              )}
              {!klipyLoading && klipyResults.length === 0 && (
                <div className="text-center py-8 text-gray-500">No GIFs found. Try a different search term.</div>
              )}
            </div>
          )}
          
          {/* Input Area - Fixed at bottom */}
          <div className={`p-3 border-t dark:border-gray-700 bg-white dark:bg-gray-800 flex gap-2 ${isMobile ? 'absolute bottom-0 left-0 right-0' : ''}`}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="image/*,video/mp4"
              className="hidden"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setShowEmojiPicker(!showEmojiPicker);
                setShowGiphyPicker(false);
              }}
              className="dark:hover:bg-gray-700 dark:text-gray-200 flex-shrink-0"
            >
              <Smile className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setShowGiphyPicker(!showGiphyPicker);
                setShowEmojiPicker(false);
              }}
              className="dark:hover:bg-gray-700 dark:text-gray-200 flex-shrink-0"
            >
              <ImageIcon className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingFile}
              className="dark:hover:bg-gray-700 dark:text-gray-200 flex-shrink-0"
            >
              <Upload className="w-5 h-5" />
            </Button>
            <Input
              placeholder="Type a message..."
              value={messageInput}
              onChange={handleInputChange}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              className="flex-1 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:placeholder-gray-400"
            />
            <Button onClick={handleSendMessage} className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 flex-shrink-0">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </TabsContent>

        {/* Games Tab */}
        <TabsContent value="games" className="flex-1 flex flex-col mt-0 p-4 data-[state=active]:flex data-[state=inactive]:hidden dark:bg-gray-800">
          {selectedGame ? (
            <div className="flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold dark:text-gray-200">{selectedGame.name}</span>
                  <span className="text-xs text-green-600 dark:text-green-400 font-semibold flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-600 dark:bg-green-400 rounded-full animate-pulse"></span>
                    Playing with {friend.username}
                  </span>
                </div>
                <Button variant="outline" size="sm" onClick={() => setSelectedGame(null)} className="dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600">
                  Close Game
                </Button>
              </div>
              <iframe
                ref={iframeRef}
                src={selectedGame.url}
                className="flex-1 border dark:border-gray-600 rounded-lg"
                title={selectedGame.name}
                style={{ resize: 'both', overflow: 'auto', minHeight: '300px' }}
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {games.map(game => (
                <div
                  key={game.id}
                  className="border dark:border-gray-600 rounded-lg p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-750"
                  onClick={() => {
                    setSelectedGame(game);
                    sendGameLaunchNotification(game.id);
                    toast.success(`${game.name} shared with ${friend.username}`);
                  }}
                >
                  <Gamepad2 className="w-8 h-8 mb-2 text-blue-600 dark:text-blue-400" />
                  <h4 className="font-semibold dark:text-gray-200">{game.name}</h4>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Video Tab */}
        <TabsContent value="video" className="flex-1 flex flex-col mt-0 p-4 data-[state=active]:flex data-[state=inactive]:hidden dark:bg-gray-800">
          <div className="space-y-3 flex-1 flex flex-col">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="dark:text-gray-200">Video URL</Label>
                {videoUrl && (
                  <span className="text-xs text-green-600 dark:text-green-400 font-semibold flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-600 dark:bg-green-400 rounded-full animate-pulse"></span>
                    Synced with {friend.username}
                  </span>
                )}
              </div>
              <Input
                placeholder="Paste any video URL..."
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && loadVideo()}
                className="dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:placeholder-gray-400"
              />
              <Button onClick={loadVideo} className="w-full bg-blue-600 hover:bg-blue-700">
                Share & Watch Together
              </Button>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                💡 Supports: Any video URL including YouTube, Vimeo, direct files (.mp4, .webm, .m3u8) and streaming links
              </p>
            </div>
            
            <div className="border dark:border-gray-600 rounded-lg flex-1 relative bg-black" style={{ minHeight: '250px' }}>
              {isExtracting ? (
                <div className="w-full h-full flex items-center justify-center text-white absolute inset-0">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-lg">Extracting video...</p>
                    <p className="text-xs text-gray-400">This may take a moment.</p>
                  </div>
                </div>
              ) : videoUrl ? (
                <>
                  {/* Use iframe for Dailymotion (ReactPlayer has known issues with it) */}
                  {isDailymotionUrl(videoUrl) ? (
                    <iframe
                      src={`https://www.dailymotion.com/embed/video/${getDailymotionVideoId(videoUrl)}?autoplay=1`}
                      width="100%"
                      height="100%"
                      style={{ position: 'absolute', top: 0, left: 0, border: 'none' }}
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title="Dailymotion Video"
                      onLoad={() => {
                        console.log('[PMWindow] Dailymotion iframe loaded');
                        setVideoReady(true);
                      }}
                    />
                  ) : videoPlayerFailed ? (
                    /* Fallback: Use iframe when ReactPlayer fails */
                    <iframe
                      src={getImageUrl(videoUrl)}
                      width="100%"
                      height="100%"
                      style={{ position: 'absolute', top: 0, left: 0, border: 'none' }}
                      allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                      allowFullScreen
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
                      title="Video Player"
                      onLoad={() => {
                        console.log('[PMWindow] Iframe fallback loaded');
                        setVideoReady(true);
                      }}
                    />
                  ) : (
                    <ReactPlayer
                      ref={videoPlayerRef}
                      url={videoUrl}
                      playing={isVideoPlaying}
                      controls={true}
                      muted={false}
                      width="100%"
                    height="100%"
                    style={{ position: 'absolute', top: 0, left: 0 }}
                    onReady={() => {
                      console.log('[PMWindow] ReactPlayer ready for URL:', videoUrl);
                      setVideoReady(true);
                    }}
                    onPlay={() => {
                      console.log('[PMWindow] Video playing');
                      setIsVideoPlaying(true);
                      // Safely get current time
                      let currentTime = 0;
                      try {
                        if (videoPlayerRef.current && typeof videoPlayerRef.current.getCurrentTime === 'function') {
                          currentTime = videoPlayerRef.current.getCurrentTime() || 0;
                        }
                      } catch (e) { console.warn('Could not get time:', e); }
                      sendVideoSync('play', currentTime, videoUrl);
                    }}
                    onPause={() => {
                      console.log('[PMWindow] Video paused');
                      setIsVideoPlaying(false);
                      // Safely get current time
                      let currentTime = 0;
                      try {
                        if (videoPlayerRef.current && typeof videoPlayerRef.current.getCurrentTime === 'function') {
                          currentTime = videoPlayerRef.current.getCurrentTime() || 0;
                        }
                      } catch (e) { console.warn('Could not get time:', e); }
                      sendVideoSync('pause', currentTime);
                    }}
                    onSeek={(seconds) => {
                      console.log('[PMWindow] Video seeked to:', seconds);
                    }}
                    onError={(e, data, hlsInstance, hlsGlobal) => {
                      console.error('[PMWindow] ReactPlayer error:', e);
                      console.error('[PMWindow] Error data:', data);
                      console.error('[PMWindow] URL that failed:', videoUrl);
                      // Try iframe fallback instead of showing error
                      console.log('[PMWindow] Switching to iframe fallback...');
                      setVideoPlayerFailed(true);
                      setVideoReady(false);
                      toast.info('Trying alternative player...');
                    }}
                    onBuffer={() => {
                      console.log('[PMWindow] Video buffering...');
                    }}
                    onBufferEnd={() => {
                      console.log('[PMWindow] Video buffer ended');
                    }}
                    config={{
                      youtube: {
                        playerVars: { 
                          showinfo: 1,
                          modestbranding: 1,
                          rel: 0,
                          origin: window.location.origin
                        }
                      },
                      vimeo: {
                        playerOptions: {
                          byline: false,
                          portrait: false,
                          controls: true
                        }
                      },
                      dailymotion: {
                        params: {
                          controls: true,
                          autoplay: false
                        }
                      },
                      facebook: {
                        appId: '',
                        version: 'v3.3',
                        playerId: 'react-player-fb'
                      },
                      file: {
                        attributes: {
                          crossOrigin: 'anonymous',
                          controlsList: 'nodownload'
                        },
                        forceVideo: true,
                        forceHLS: videoUrl?.includes('.m3u8'),
                        forceDASH: videoUrl?.includes('.mpd'),
                        hlsOptions: {
                          enableWorker: true,
                          lowLatencyMode: true
                        }
                      }
                    }}
                    fallback={
                      <div className="w-full h-full flex items-center justify-center bg-gray-900">
                        <div className="text-center text-white">
                          <p className="text-lg mb-2">⚠️ Video cannot be embedded</p>
                          <a 
                            href={videoUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-400 underline"
                          >
                            Open video in new tab
                          </a>
                        </div>
                      </div>
                    }
                  />
                  )}
                  <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded shadow-lg z-10">
                    {videoReady ? '✅ Ready - Synced playback' : videoPlayerFailed ? '🔄 Using fallback player' : '⏳ Loading...'}
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 absolute inset-0">
                  <div className="text-center">
                    <p className="text-4xl mb-3">🎬</p>
                    <p className="text-sm">Paste a video URL above to start watching together</p>
                    <p className="text-xs mt-2 text-gray-500">Supports any video URL including direct .mp4, .m3u8 streams & more</p>
                  </div>
                </div>
              )}
            </div>

            {videoUrl && (
              <div className="flex gap-2">
                <Button 
                  onClick={() => handleVideoAction('play')} 
                  className="flex-1"
                  disabled={isVideoPlaying}
                >
                  ▶ Play
                </Button>
                <Button 
                  onClick={() => handleVideoAction('pause')} 
                  className="flex-1"
                  disabled={!isVideoPlaying}
                >
                  ⏸ Pause
                </Button>
                <Button 
                  onClick={() => handleVideoAction('stop')} 
                  className="flex-1"
                  variant="outline"
                >
                  ⏹ Stop
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Options Tab */}
        <TabsContent value="options" className="flex-1 flex flex-col mt-0 p-4 space-y-4 data-[state=active]:flex data-[state=inactive]:hidden overflow-y-auto dark:bg-gray-800">
          <div className="space-y-3">
            <h3 className="font-semibold dark:text-gray-200">Background</h3>
            <div className="space-y-2">
              <Label className="dark:text-gray-200">Background URL</Label>
              <Input
                placeholder="Enter background URL..."
                value={backgroundUrl}
                onChange={(e) => setBackgroundUrl(e.target.value)}
                className="dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:placeholder-gray-400"
              />
              <Button onClick={() => updateBackground(true)} className="w-full dark:bg-blue-600 dark:hover:bg-blue-700">Set URL Background</Button>
            </div>

            <div className="space-y-2">
              <Label className="dark:text-gray-200">Upload Background Image (Max 5MB)</Label>
              <Input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif"
                onChange={(e) => setBackgroundFile(e.target.files[0])}
                className="dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
              />
              <Button onClick={() => updateBackground(false)} className="w-full dark:bg-blue-600 dark:hover:bg-blue-700">Upload Background</Button>
            </div>
          </div>

          <div className="border-t dark:border-gray-700 pt-4 space-y-2">
            <h3 className="font-semibold dark:text-gray-200">Chat Management</h3>
            <Button variant="outline" onClick={clearChat} className="w-full dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600">
              Clear Chat
            </Button>
            <Button variant="outline" onClick={clearBackground} className="w-full dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600">
              Clear Background
            </Button>
          </div>

          <div className="border-t dark:border-gray-700 pt-4 space-y-2">
            <h3 className="font-semibold text-red-600 dark:text-red-400">Danger Zone</h3>
            <Button variant="destructive" onClick={unfriend} className="w-full dark:bg-red-700 dark:hover:bg-red-800">
              <UserX className="w-4 h-4 mr-2" />
              Unfriend
            </Button>
            <Button variant="destructive" onClick={blockUser} className="w-full dark:bg-red-700 dark:hover:bg-red-800">
              <Ban className="w-4 h-4 mr-2" />
              Block User
            </Button>
            <Button variant="outline" onClick={onClose} className="w-full dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600">
              Disconnect PM
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Media Lightbox for full-size viewing */}
      {lightboxMedia && (
        <MediaLightbox 
          media={lightboxMedia} 
          onClose={() => setLightboxMedia(null)}
        />
      )}

      {/* URL Viewer Modal for clickable links */}
      <URLViewerModal
        isOpen={urlModalOpen}
        onClose={() => setUrlModalOpen(false)}
        url={viewUrl}
      />
    </div>
  );
};

export default PMWindow;
